import { useQuery } from "@tanstack/react-query";
import {
  getCities,
  getCountries,
  getFullCountries,
} from "../../../../API/common/sharedServices";

import Services from "../../../../API/PIservices";
import LegalServices from "../../../../API/LegalFormService";
import {
  ACCOUNT_PURPOSES_VALUE,
  cityOrVillage,
  selectValuesFromResponse,
} from "../step3/utils";

function useGetDataForForm({ locale, addressAndPepOnly = false }) {
  const { isLoading: fullCountriesLoading, data: fullCountries } = useQuery({
    queryKey: ["fullCountries"],
    queryFn: getFullCountries,
    select: (res) => {
      return res?.data?.map((item) => {
        const label =
          locale === "en" ? item?.referenceNameEn : item?.referenceNameGe;

        return {
          value: item?.["referenceId"],
          label,
          additionalInfo: item?.additionalInfo,
        };
      });
    },
  });

  const { isLoading: countriesLoading, data: countries } = useQuery({
    queryKey: ["countries"],
    enabled: !addressAndPepOnly,
    queryFn: getCountries,
    select: (res) => selectValuesFromResponse(res?.data, locale),
  });

  const { isLoading: creditDebitCountriesLoading, data: creditDebitCountries } =
    useQuery({
      queryKey: ["creditDebitCountries"],
    enabled: !addressAndPepOnly,
      queryFn: Services.geCreditDebitCountries,
      select: (res) => selectValuesFromResponse(res?.data, locale),
    });

  const { isLoading: citiesLoading, data: cities } = useQuery({
    queryKey: ["cities"],
    queryFn: getCities,
    select: (res) => selectValuesFromResponse(res?.data, locale),
  });

  const { isLoading: employMentStatusesLoading, data: employMentStatuses } =
    useQuery({
      queryKey: ["employMentStatuses"],
    enabled: !addressAndPepOnly,
      queryFn: Services.getEmploymentStatuses,
      select: (res) => selectValuesFromResponse(res?.data, locale),
    });

  const { isLoading: moneySourcesLoading, data: moneySources } = useQuery({
    queryKey: ["moneySources"],
    enabled: !addressAndPepOnly,
    queryFn: Services.getPIMoneySources,
    select: (res) => selectValuesFromResponse(res?.data, locale),
  });

  const { isLoading: incomesLoading, data: incomes } = useQuery({
    queryKey: ["incomes"],
    enabled: !addressAndPepOnly,
    queryFn: Services.getPIIncomes,
    select: (res) => selectValuesFromResponse(res?.data, locale),
  });

  const { isLoading: monthlyTurnoversLoading, data: monthlyTurnovers } =
    useQuery({
      queryKey: ["monthlyTurnovers"],
    enabled: !addressAndPepOnly,
      queryFn: Services.GetMonthlyTurnovers,
      select: (res) => selectValuesFromResponse(res?.data, locale),
    });

  const { isLoading: activitySubjectsLoading, data: activitySubjects } =
    useQuery({
      queryKey: ["activitySubjects"],
    enabled: !addressAndPepOnly,
      queryFn: Services.getActivitySubject,
      select: (res) => selectValuesFromResponse(res?.data, locale),
    });

  const {
    isLoading: economicActivityTypesLoading,
    data: economicActivityTypes,
  } = useQuery({
    queryKey: ["economicActivityTypes"],
    enabled: !addressAndPepOnly,
    queryFn: LegalServices.getEconomicActivityTypes,
    select: (res) => {
      return res?.data?.map((item) => {
        const label =
          locale === "en" ? item?.referenceNameEn : item?.referenceNameGe;

        return {
          value: item?.["additionalInfo"],
          label,
          kycScore: item?.referenceId,
        };
      });
    },
  });

  const { isLoading: accountPurposesLoading, data: accountPurposes } = useQuery(
    {
      queryKey: ["accountPurposes"],
      enabled: !addressAndPepOnly,
      queryFn: Services.getPIAccountPurposes,
      select: (res) => {
        return res?.data?.map((item) => {
          const label =
            locale === "en" ? item?.referenceNameEn : item?.referenceNameGe;

          const isAccount =
            ACCOUNT_PURPOSES_VALUE.accounts === item?.["referenceId"];

          return {
            value: item?.["referenceId"],
            label,
            isDisabled: isAccount ? true : false,
          };
        });
      },
    }
  );

  const { isLoading: taxClassificationsLoading, data: texClassifications } =
    useQuery({
      queryKey: ["texClassifications"],
    enabled: !addressAndPepOnly,
      queryFn: Services.getTaxClassifications,
      select: (res) => selectValuesFromResponse(res?.data, locale),
    });

  const { isLoading: pepCategoryLoading, data: pepCategory } = useQuery({
    queryKey: ["pepCategory"],
    queryFn: Services.getPepCategory,
    select: (res) => selectValuesFromResponse(res?.data, locale),
  });

  const { isLoading: pepTypesLoading, data: pepTypes } = useQuery({
    queryKey: ["pepTypes"],
    enabled: !addressAndPepOnly,
    queryFn: Services.getPeps,
    select: (res) => selectValuesFromResponse(res?.data, locale),
  });

  const { isLoading: assetsLoading, data: assets } = useQuery({
    queryKey: ["assets"],
    enabled: !addressAndPepOnly,
    queryFn: Services.getAssets,
    select: (res) => selectValuesFromResponse(res?.data, locale),
  });

  const { isLoading: propertySourcesLoading, data: propertySources } = useQuery(
    {
      queryKey: ["propertySources"],
    enabled: !addressAndPepOnly,
      queryFn: Services.GetPropertieSources,
      select: (res) => selectValuesFromResponse(res?.data, locale),
    }
  );

  const { isLoading: moneySourcesOptionsLoading, data: moneySourcesOptions } =
    useQuery({
      queryKey: ["moneySourcesOptions"],
    enabled: !addressAndPepOnly,
      queryFn: Services.GetMoneySources,
      select: (res) => selectValuesFromResponse(res?.data, locale),
    });

  return {
    isLoading:
      fullCountriesLoading ||
      citiesLoading ||
      employMentStatusesLoading ||
      moneySourcesLoading ||
      incomesLoading ||
      monthlyTurnoversLoading ||
      countriesLoading ||
      activitySubjectsLoading ||
      economicActivityTypesLoading ||
      accountPurposesLoading ||
      taxClassificationsLoading ||
      pepCategoryLoading ||
      pepTypesLoading ||
      assetsLoading ||
      propertySourcesLoading ||
      moneySourcesOptionsLoading ||
      creditDebitCountriesLoading,
    transFormedCityOrVillage: selectValuesFromResponse(cityOrVillage, locale),
    economicActivityTypes,
    creditDebitCountries,
    moneySourcesOptions,
    texClassifications,
    employMentStatuses,
    monthlyTurnovers,
    activitySubjects,
    propertySources,
    accountPurposes,
    fullCountries,
    moneySources,
    pepCategory,
    countries,
    pepTypes,
    incomes,
    assets,
    cities,
  };
}

export { useGetDataForForm };
